export type BoundBootstrapProfile = {
  id: string;
  authUid?: string;
  role?: string;
  unitIds?: string[];
  accessProfileVersion?: number;
};

export type BoundBootstrapResponse<T extends BoundBootstrapProfile> =
  | { ok: true; authorityReady?: boolean; bindingGeneration?: number; userId: string; user?: T }
  | { ok: false; reason: 'not-found' };

// A display-complete users/{authUid} mirror is not a bootstrap authority source.
// Login, restored sessions and manual retries all await the same server path.
// The readiness marker also prevents an older, not-yet-updated callable from
// silently restoring the pre-E2 fallback during a staggered deployment.
export async function loadBoundBootstrapProfile<T extends BoundBootstrapProfile>(
  authUid: string,
  resolve: () => Promise<BoundBootstrapResponse<T>>,
): Promise<{ userId: string; user: T } | null> {
  const response = await resolve();
  if (!response.ok) return null;
  const user = response.user;
  if (response.authorityReady !== true || !Number.isSafeInteger(response.bindingGeneration)
    || (response.bindingGeneration ?? 0) < 1 || !response.userId || response.userId.includes('/')
    || !user || user.id !== response.userId || user.authUid !== authUid
    || typeof user.role !== 'string' || !user.role.trim()
    || !Array.isArray(user.unitIds) || user.unitIds.some(id => typeof id !== 'string' || !id.trim())
    || !Number.isSafeInteger(user.accessProfileVersion) || (user.accessProfileVersion ?? -1) < 0) {
    throw Object.assign(new Error('The current bound access profile is not ready.'), { code: 'failed-precondition' });
  }
  return { userId: response.userId, user };
}
